import React, {useEffect, useRef, useState} from 'react';

import {BaseProps} from "../../../infrastructure/models/props/base-props";
import {VIDEO_EMBEDDED} from "./video-embedded.consts";
import {VideoDomainModel} from "../../../domain/full-game/models/video/video-domain-model";
import {UseIoC} from "../composers/composer-plugins/use-ioc";
import {IOC} from "../../../infrastructure/ioc";
import {StringToJsx} from "../string-to-jsx";
import styles from "./video-embedded.module.scss";
import classNames from "classnames"
import {YoutubeVideoManager} from "../../managers/youtube-video.manager";
import {VideoEmbeddedCardTitle} from "./video-embedded.card-title";
import {VideoMicrodataSchema} from "./video-microdata-schema";

export class VideoEmbeddedProps extends BaseProps {
  title: String;
  hasVideo: Boolean;
  video: VideoDomainModel;
  statusGroup: Number;
  gameId: Number;
  ioc: IOC;
  containerClassName: String;
  onVideoStateChange: Function;
}

export const VideoEmbedded = UseIoC()(({
                                         title,
                                         hasVideo,
                                         video,
                                         statusGroup,
                                         gameId,
                                         device,
                                         ioc,
                                         containerClassName,
                                         width: propsWidth,
                                         shouldRenderCardWrapper = true,
                                         shouldAutoPlay = false,
                                         useMicroData = false,
                                         onVideoStateChange,
                                         getVideo
                                       }: VideoEmbeddedProps) => {
  const youtubeManager: YoutubeVideoManager = ioc.getInstance(YoutubeVideoManager);
  if (!hasVideo || !video?.isEmbedded) {
    return null;
  }

  const [showVideo, setShowVideo] = useState(false);
  const {embedElement, id, isYouTubeVideo} = video;
  const ref = React.createRef();

  const [videoElement, setVideoElement] = useState(embedElement);
  const [width, setWidth] = useState(propsWidth || '100%');
  const [height, setHeight] = useState(VIDEO_EMBEDDED.defaultHeight);

  useEffect(() => {
    const refWidth: Number = ref.current?.parentElement.getBoundingClientRect().width;
    const refHeight = parseInt(refWidth * VIDEO_EMBEDDED.ratio);
    setWidth(refWidth);
    setHeight(refHeight);

    if (isYouTubeVideo) {
      youtubeManager.initVideoPlayer(refHeight, refWidth, id, statusGroup, gameId);
      youtubeManager.registerToStateChange(id, (status) => {
        setShowVideo(status >= 0);
        onVideoStateChange && onVideoStateChange(status);
      });
      youtubeManager.setAutoplay(shouldAutoPlay);
      getVideo?.(youtubeManager);
    }

    return () => {
      if (isYouTubeVideo) {
        youtubeManager?.unMountVideoEvent(gameId, id, statusGroup);
        youtubeManager.unregisterCallback(id);
        youtubeManager.setAutoplay(false);
      }
      return true;
    };
  }, [])

  useEffect(() => {
    showVideo && setVideoElement(setVideoSize(embedElement))
  }, [showVideo, ref.current]);


  const isFirstRender = useRef(true);

  useEffect(() => {
    //only switch between videos if not the first time player is available
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!showVideo && !video?.id) {
      return;
    }
    youtubeManager.switchVideo(video?.id, statusGroup, gameId);
  }, [video?.id]);

  const setVideoSize: String = (video: String) => video?.format({'w': width, 'h': height, 'id': id});

  const containerProps = {
    ref,
    className: classNames(styles.container, containerClassName, {
      [styles.visible_container]: showVideo,
      [styles.small_view]: device.isSmallView
    })
  }

  const titleProps = {
    title,
    device,
    shouldRenderCardWrapper
  }

  return (
    <div {...containerProps}>
      <VideoEmbeddedCardTitle {...titleProps}>
        <div className={classNames(styles.video)}>
          {
            isYouTubeVideo ? <div id={id}></div> : <StringToJsx domString={videoElement}/>
          }
        </div>
      </VideoEmbeddedCardTitle>
      {
        useMicroData && video ? <VideoMicrodataSchema ioc={ioc} videoId={id}/> : null
      }
    </div>
  )
});
